import styled from 'styled-components';

const colors = {
  ink: '#1A1814',
  ink2: '#221F1A',
  cream: '#F3ECE1',
  cream2: '#EBE3D6',
  red: '#D9442B',
  body: '#4A453D',
  muted: '#6B645A',
  faint: '#8A8276',
  rule: '#D2C8B8',
  line: '#3A342C',
  text: '#CFC6B8',
  dim: '#A89E8F',
};

const fonts = {
  display: "'Instrument Serif', Georgia, serif",
  serif: "'Newsreader', Georgia, serif",
  mono: "'IBM Plex Mono', ui-monospace, Menlo, monospace",
  ui: "'IBM Plex Sans', 'Helvetica Neue', Arial, sans-serif",
};

export const PageWrapper = styled.div`
  background: ${colors.cream};
  min-height: 100vh;
  padding-top: 88px;

  @media (max-width: 768px) {
    padding-top: 60px;
  }
`;

export const Wrap = styled.div`
  max-width: 1440px;
  margin: 0 auto;
  padding-inline: clamp(20px, 4.5vw, 64px);
`;

export const Eyebrow = styled.p`
  font: 400 12px ${fonts.mono};
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: ${colors.red};
  margin: 0;
`;

export const HeroSection = styled.section`
  padding: 88px 0 56px;
  max-width: 800px;

  @media (max-width: 900px) {
    padding-top: 56px;
  }
`;

export const HeroTitle = styled.h1`
  margin-top: 22px;
  font-family: ${fonts.display};
  font-weight: 400;
  font-size: clamp(52px, 6.4vw, 92px);
  line-height: 0.95;
  letter-spacing: -0.015em;

  em {
    color: ${colors.red};
    font-style: normal;
  }
`;

export const HeroSub = styled.p`
  margin: 24px 0 0;
  font-size: 20px;
  color: ${colors.body};
  max-width: 32em;
  font-family: ${fonts.serif};
  line-height: 1.55;
`;

export const FiltersContainer = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  margin-top: 48px;
`;

interface FilterChipProps {
  $active?: boolean;
}

export const FilterChip = styled.button<FilterChipProps>`
  font: 400 11px ${fonts.mono};
  letter-spacing: 0.14em;
  text-transform: uppercase;
  background: ${props => props.$active ? colors.ink : 'transparent'};
  color: ${props => props.$active ? colors.cream : colors.body};
  border: 1px solid ${props => props.$active ? colors.ink : colors.rule};
  padding: 10px 14px 9px;
  cursor: pointer;
  transition: all 0.15s ease;

  span {
    color: ${colors.red};
    margin-left: 6px;
  }

  &:hover {
    border-color: ${colors.ink};
    color: ${props => props.$active ? colors.cream : colors.ink};
  }

  &:focus-visible {
    outline: 2px solid ${colors.red};
    outline-offset: 3px;
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

export const ResourcesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(340px, 1fr));
  gap: 32px;
  margin-top: 32px;
  padding-bottom: 88px;

  /* Make all cards equal height */
  & > div {
    display: flex;
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 24px;
    margin-top: 48px;
    padding-bottom: 64px;
  }
`;

export const ResourceCard = styled.a`
  display: flex;
  flex-direction: column;
  flex: 1;
  background: ${colors.cream2};
  border: 1px solid ${colors.rule};
  padding: 32px;
  text-decoration: none;
  color: inherit;
  transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 12px 32px rgba(26, 24, 20, 0.08);
    border-color: ${colors.ink};
  }

  &:focus-visible {
    outline: 2px solid ${colors.red};
    outline-offset: 3px;
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

export const CardEyebrow = styled.span`
  display: inline-block;
  font: 400 10px ${fonts.mono};
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: ${colors.muted};
  border: 1px solid ${colors.rule};
  padding: 6px 10px 5px;
  margin-bottom: 20px;
`;

export const CardTitle = styled.h2`
  font-family: ${fonts.display};
  font-weight: 400;
  font-size: clamp(28px, 3vw, 36px);
  line-height: 1.05;
  letter-spacing: -0.015em;
  margin: 0;

  em {
    color: ${colors.red};
    font-style: normal;
  }
`;

export const CardDescription = styled.p`
  margin: 16px 0 0;
  font-size: 17px;
  color: ${colors.body};
  font-family: ${fonts.serif};
  line-height: 1.55;
`;

export const CardMeta = styled.div`
  margin-top: 24px;
  display: flex;
  gap: 20px;
  flex-wrap: wrap;
  font: 400 11px ${fonts.mono};
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: ${colors.muted};

  b {
    color: ${colors.ink};
    font-weight: 400;
  }
`;

export const CardArrow = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  margin-top: auto;
  padding-top: 28px;
  font: 500 14px ${fonts.ui};
  color: ${colors.ink};

  span {
    color: ${colors.red};
    transition: transform 0.2s ease;
  }

  ${ResourceCard}:hover & span {
    transform: translateX(4px);
  }

  @media (prefers-reduced-motion: reduce) {
    span {
      transition: none;
    }
  }
`;

export const CardImage = styled.div`
  margin: -32px -32px 24px -32px;
  overflow: hidden;
  border-bottom: 1px solid ${colors.rule};

  img {
    width: 100%;
    height: 200px;
    object-fit: cover;
    display: block;
    transition: transform 0.3s ease;
  }

  ${ResourceCard}:hover & img {
    transform: scale(1.02);
  }

  @media (prefers-reduced-motion: reduce) {
    img {
      transition: none;
    }
  }

  @media (max-width: 768px) {
    margin: -32px -32px 20px -32px;

    img {
      height: 180px;
    }
  }
`;

export const SectionDivider = styled.div`
  border-top: 1px solid ${colors.rule};
  margin: 64px 0;
`;

export const SectionTitle = styled.h2`
  font-family: ${fonts.display};
  font-weight: 400;
  font-size: clamp(36px, 4vw, 52px);
  line-height: 1;
  letter-spacing: -0.015em;
  margin: 0 0 32px;

  em {
    color: ${colors.red};
    font-style: normal;
  }
`;

export const FooterSection = styled.footer`
  border-top: 1px solid ${colors.rule};
  padding: 28px 0 40px;
  display: flex;
  justify-content: space-between;
  font: 400 11px ${fonts.mono};
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: ${colors.muted};

  a {
    text-decoration: none;
    color: inherit;

    &:focus-visible {
      outline: 2px solid ${colors.red};
      outline-offset: 3px;
    }
  }
`;
